/*
 * Tailwind UI Component: Application UI > Elements > Buttons
 * https://tailwindui.com/components/application-ui/elements/buttons
 */

import * as React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';

// Base styles that apply to all buttons
const baseStyles = 'btn';

// Color variants
const colorMap = {
  primary: 'btn-primary',
  secondary: 'bg-white text-primary',
  white:
    'btn bg-primary-content text-primary hover:scale-105 transition-all duration-300 px-4',
  ghost: 'btn-ghost',
  linkPrimary: 'btn-link',
  linkWhite: 'btn-link text-white',
  transparent: 'btn-ghost p-4',
};

// Size variants
const sizeMap = {
  xs: 'btn-[2xs] font-base', // Going smaller than xs
  sm: 'btn-xs font-base',
  md: 'btn-sm font-base',
  lg: 'btn-md font-base',
  xl: 'btn-lg font-base',
  xxl: 'btn-lg font-base', // DaisyUI doesn't have xl or xxl, using lg instead
};

// Responsive width classes
const fullWidthMap = {
  base: '',
  mobile: 'btn-block md:!w-auto lg:!w-auto xl:!w-auto',
  tablet: '',
  desktop: '',
  widescreen: '',
};

export default function Button(props) {
  const {
    type,
    children,
    color = 'primary',
    base = 'xl',
    mobile,
    tablet,
    desktop,
    widescreen,
    fullWidth,
    internalLinkTo,
    externalLinkTo,
    emailLinkTo,
    onClick,
    noPadding,
    openInNewTab,
    className,
  } = props;

  // Compile all the classes
  const buttonClasses = [
    baseStyles,
    colorMap[color],
    sizeMap[base],
    mobile && `sm:${sizeMap[mobile]}`,
    tablet && `md:${sizeMap[tablet]}`,
    desktop && `lg:${sizeMap[desktop]}`,
    widescreen && `xl:${sizeMap[widescreen]}`,
    fullWidth && fullWidthMap[fullWidth],
    noPadding && 'p-0',
  ]
    .filter(Boolean)
    .join(' ');

  // If the button links to a page on apcon.com, render Gatsby's Link component
  if (internalLinkTo) {
    return (
      <Link
        to={internalLinkTo}
        className={buttonClasses + ' no-underline' + className}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }

  // If the button links to another website and should open in a new tab
  if (externalLinkTo && openInNewTab) {
    return (
      <a
        href={externalLinkTo}
        className={buttonClasses + ' ' + className}
        onClick={onClick}
        target="_blank"
        rel="nofollow noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  // If the button links to another website
  if (externalLinkTo) {
    return (
      <a
        href={externalLinkTo}
        className={buttonClasses + ' ' + className}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  // If the button is an email link
  if (emailLinkTo) {
    return (
      <a
        href={`mailto:${emailLinkTo}`}
        className={buttonClasses + ' ' + className}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  // If it's not a link, render a button
  return (
    <button
      onClick={onClick}
      type={type}
      className={buttonClasses + ' ' + className}
    >
      {children}
    </button>
  );
}

Button.propTypes = {
  type: PropTypes.oneOf(['submit', 'button', 'reset']),
  children: PropTypes.node.isRequired,
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'white',
    'ghost',
    'linkPrimary',
    'linkWhite',
  ]),
  base: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  mobile: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  tablet: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  desktop: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  widescreen: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  fullWidth: PropTypes.oneOf([
    'base',
    'mobile',
    'tablet',
    'desktop',
    'widescreen',
  ]),
  internalLinkTo: PropTypes.string,
  externalLinkTo: PropTypes.string,
  emailLinkTo: PropTypes.string,
  noPadding: PropTypes.bool,
  openInNewTab: PropTypes.bool,
};
